<template>
  <div ref="editorRoot" class="relative editor-root">
    <tag-agents
      v-if="showUserMentions && isPrivate"
      :search-key="mentionSearchKey"
      @click="insertMentionNode"
    />
    <canned-response
      v-if="shouldShowCannedResponses"
      :search-key="cannedSearchTerm"
      @click="insertCannedResponse"
    />
    <variable-list
      v-if="shouldShowVariables"
      :search-key="variableSearchTerm"
      @click="insertVariable"
    />
    <input
      ref="imageUpload"
      type="file"
      accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
      hidden
      @change="onFileChange"
    />
    <div ref="editor" />
    <div
      v-show="isImageNodeSelected && showImageResizeToolbar"
      class="absolute shadow-md rounded-[4px] flex gap-1 py-1 px-1 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-50"
      :style="{
        top: toolbarPosition.top,
        left: toolbarPosition.left,
      }"
    >
      <button
        v-for="size in sizes"
        :key="size.name"
        class="text-xs font-medium rounded-[4px] border border-solid border-slate-200 dark:border-slate-600 px-1.5 py-0.5 hover:bg-slate-100 dark:hover:bg-slate-800"
        @click="setURLWithQueryAndImageSize(size)"
      >
        {{ size.name }}
      </button>
    </div>
    <slot name="footer" />
  </div>
</template>

<script>
import {
  createMessageSchema,
  buildEditor,
  EditorView,
  MessageMarkdownTransformer,
  EditorState,
  Selection,
} from './config';

import {
  suggestionsPlugin,
  triggerCharacters,
} from '@chatwoot/prosemirror-schema/src/mentions/plugin';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import TagAgents from '../../conversation/TagAgents.vue';
import CannedResponse from '../../conversation/CannedResponse.vue';
import VariableList from '../../conversation/VariableList.vue';
import {
  appendSignature,
  removeSignature,
  escapeSignatureInBody,
  EditorMarkdownSerializer,
  insertAtCursor,
  scrollCursorIntoView,
  findNodeToInsertImage,
  setURLWithQueryAndSize,
} from 'dashboard/helper/editorHelper';
const TYPING_INDICATOR_IDLE_TIME = 500;
const MAXIMUM_FILE_UPLOAD_SIZE = 4; // in MB

import {
  hasPressedEnterAndNotCmdOrShift,
  hasPressedCommandAndEnter,
  hasPressedAltAndPKey,
  hasPressedAltAndLKey,
} from 'shared/helpers/KeyboardHelpers';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import { isEditorHotKeyEnabled } from 'dashboard/mixins/uiSettings';
import { replaceVariablesInMessage } from '@chatwoot/utils';
import { CONVERSATION_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import alertMixin from 'shared/mixins/alertMixin';
import {
  MESSAGE_EDITOR_MENU_OPTIONS,
  MESSAGE_EDITOR_IMAGE_RESIZES,
} from 'dashboard/constants/editor';
import { keymap } from 'prosemirror-keymap';
import {
  chainCommands,
  createParagraphNear,
  liftEmptyBlock,
  newlineInCode,
  splitBlock,
} from 'prosemirror-commands';

const createState = (
  content,
  placeholder,
  // eslint-disable-next-line default-param-last
  plugins = [],
  // eslint-disable-next-line default-param-last
  methods = {},
  enabledMenuOptions,
  defaultDirection = 'ltr',
  autoDetectLanguage = true
) => {
  const schema = createMessageSchema(defaultDirection);
  return EditorState.create({
    doc: new MessageMarkdownTransformer(schema).parse(content),
    plugins: buildEditor({
      schema,
      placeholder,
      methods,
      plugins,
      enabledMenuOptions,
      defaultDirection,
      autoDetectLanguage,
    }),
  });
};

export default {
  name: 'WootMessageEditor',
  components: { TagAgents, CannedResponse, VariableList },
  mixins: [eventListenerMixins, uiSettingsMixin, alertMixin],
  props: {
    focused: { type: Boolean, default: true },
    value: { type: String, default: '' },
    editorId: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    isPrivate: { type: Boolean, default: false },
    enableSuggestions: { type: Boolean, default: true },
    overrideLineBreaks: { type: Boolean, default: false },
    updateSelectionWith: { type: String, default: '' },
    enableVariables: { type: Boolean, default: false },
    enableCannedResponses: { type: Boolean, default: true },
    variables: { type: Object, default: () => ({}) },
    enabledMenuOptions: { type: Array, default: () => [] },
    signature: { type: String, default: '' },
    // allowSignature is a kill switch, ensuring no signature methods
    // are triggered except when this flag is true
    allowSignature: { type: Boolean, default: false },
    showImageResizeToolbar: { type: Boolean, default: false }, // A kill switch to show or hide the image toolbar
    autoDetectLanguage: { type: Boolean, default: true },
  },
  data() {
    return {
      showUserMentions: false,
      showCannedMenu: false,
      showVariables: false,
      mentionSearchKey: '',
      cannedSearchTerm: '',
      variableSearchTerm: '',
      editorView: null,
      range: null,
      state: undefined,
      isImageNodeSelected: false,
      toolbarPosition: { top: 0, left: 0 },
      sizes: MESSAGE_EDITOR_IMAGE_RESIZES,
      selectedImageNode: null,
    };
  },
  computed: {
    defaultDirection() {
      return 'ltr';
    },
    editorRoot() {
      return this.$refs.editorRoot;
    },
    contentFromEditor() {
      return EditorMarkdownSerializer.serialize(this.editorView.state.doc);
    },
    shouldShowVariables() {
      return this.enableVariables && this.showVariables && !this.isPrivate;
    },
    shouldShowCannedResponses() {
      return (
        this.enableCannedResponses && this.showCannedMenu && !this.isPrivate
      );
    },
    editorMenuOptions() {
      return this.enabledMenuOptions.length
        ? this.enabledMenuOptions
        : MESSAGE_EDITOR_MENU_OPTIONS;
    },
    plugins() {
      let enabledPlugins = [];
      if (this.enableSuggestions) {
        enabledPlugins = enabledPlugins.concat([
          suggestionsPlugin({
            matcher: triggerCharacters('@'),
            onEnter: args => {
              this.showUserMentions = true;
              this.range = args.range;
              this.editorView = args.view;
              return false;
            },
            onChange: args => {
              this.editorView = args.view;
              this.range = args.range;

              this.mentionSearchKey = args.text.replace('@', '');

              return false;
            },
            onExit: () => {
              this.mentionSearchKey = '';
              this.showUserMentions = false;
              return false;
            },
            onKeyDown: ({ event }) => {
              return event.keyCode === 13 && this.showUserMentions;
            },
          }),
          suggestionsPlugin({
            matcher: triggerCharacters('/'),
            suggestionClass: '',
            onEnter: args => {
              if (this.isPrivate) {
                return false;
              }
              this.showCannedMenu = true;
              this.range = args.range;
              this.editorView = args.view;
              return false;
            },
            onChange: args => {
              this.editorView = args.view;
              this.range = args.range;

              this.cannedSearchTerm = args.text.replace('/', '');
              return false;
            },
            onExit: () => {
              this.cannedSearchTerm = '';
              this.showCannedMenu = false;
              return false;
            },
            onKeyDown: ({ event }) => {
              return event.keyCode === 13 && this.showCannedMenu;
            },
          }),
          suggestionsPlugin({
            matcher: triggerCharacters('{{'),
            suggestionClass: '',
            onEnter: args => {
              if (this.isPrivate) {
                return false;
              }
              this.showVariables = true;
              this.range = args.range;
              this.editorView = args.view;
              return false;
            },
            onChange: args => {
              this.editorView = args.view;
              this.range = args.range;

              this.variableSearchTerm = args.text.replace('{{', '');
              return false;
            },
            onExit: () => {
              this.variableSearchTerm = '';
              this.showVariables = false;
              return false;
            },
            onKeyDown: ({ event }) => {
              return event.keyCode === 13 && this.showVariables;
            },
          }),
        ]);
      }
      const enterCommands = [
        newlineInCode,
        createParagraphNear,
        (state, dispatch) => {
          dispatch(
            state.tr
              .insertText(` `)
              .replaceSelectionWith(state.schema.nodes.hard_break.create())
              .scrollIntoView()
          );
          return true;
        },
        liftEmptyBlock,
        splitBlock,
      ];

      return enabledPlugins.concat([
        // commented out because breaks default behavior of editor with creating new <p> tag on Enter, which leads to alignment menu items issues
        // keymap({
        //   Enter: chainCommands.apply(null, enterCommands),
        // }),
      ]);
    },
    sendWithSignature() {
      return !!this.signature && this.allowSignature && !this.isPrivate;
    },
  },
  watch: {
    showUserMentions(updatedValue) {
      this.$emit('toggle-user-mention', this.isPrivate && updatedValue);
    },
    showCannedMenu(updatedValue) {
      this.$emit('toggle-canned-menu', !this.isPrivate && updatedValue);
    },
    showVariables(updatedValue) {
      this.$emit('toggle-variables-menu', !this.isPrivate && updatedValue);
    },
    value(newVal = '') {
      if (newVal !== this.contentFromEditor) {
        this.reloadState(newVal);
      }
    },
    editorId() {
      this.showCannedMenu = false;
      this.cannedSearchTerm = '';
      this.reloadState(this.value);
    },
    isPrivate() {
      this.reloadState(this.value);
    },
    updateSelectionWith(newValue, oldValue) {
      if (!this.editorView) {
        return null;
      }
      if (newValue !== oldValue) {
        if (this.updateSelectionWith !== '') {
          const node = this.editorView.state.schema.text(
            this.updateSelectionWith
          );
          const tr = this.editorView.state.tr.replaceSelectionWith(node);
          this.editorView.focus();
          this.state = this.editorView.state.apply(tr);
          this.editorView.updateState(this.state);
          this.emitOnChange();
          this.$emit('clear-selection');
        }
      }
      return null;
    },
    sendWithSignature(newValue) {
      // see if the allowSignature flag is true
      if (this.allowSignature) {
        this.toggleSignatureInEditor(newValue);
      }
    },
  },
  created() {
    this.state = createState(
      escapeSignatureInBody(this.value, this.signature),
      this.placeholder,
      this.plugins,
      { onImageUpload: this.openFileBrowser },
      this.editorMenuOptions,
      this.defaultDirection,
      this.autoDetectLanguage
    );
  },
  mounted() {
    this.createEditorView();
    this.editorView.updateState(this.state);
    if (this.focused) this.focusEditorInputField();

    // BUS Event to insert text or markdown into the editor at the
    // current cursor position.
    // Components using this
    // 1. SearchPopover.vue

    bus.$on(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, this.insertContentIntoEditor);
  },
  beforeDestroy() {
    bus.$off(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, this.insertContentIntoEditor);
  },
  methods: {
    reloadState(content = this.value) {
      this.state = createState(
        escapeSignatureInBody(content, this.signature),
        this.placeholder,
        this.plugins,
        { onImageUpload: this.openFileBrowser },
        this.editorMenuOptions,
        this.defaultDirection
      );
      this.editorView.updateState(this.state);

      this.focusEditor(content);
    },
    focusEditor(content) {
      if (this.isBodyEmpty(content) && this.sendWithSignature) {
        // reload state can be called when switching between conversations, or when drafts is loaded
        // these drafts can also have a signature, so we need to check if the body is empty
        // and handle things accordingly
        this.handleEmptyBodyWithSignature();
      } else {
        // this is in the else block, handleEmptyBodyWithSignature also has a call to the focus method
        // the position is set to start, because the signature is added at the end of the body
        this.focusEditorInputField('end');
      }
    },
    toggleSignatureInEditor(signatureEnabled) {
      // The toggleSignatureInEditor gets the new value from the
      // watcher, this means that if the value is true, the signature
      // is supposed to be added, else we remove it.
      if (signatureEnabled) {
        this.addSignature();
      } else {
        this.removeSignature();
      }
    },
    addSignature() {
      let content = this.value;
      // see if the content is empty, if it is before appending the signature
      // we need to add a paragraph node and move the cursor at the start of the editor
      const contentWasEmpty = this.isBodyEmpty(content);
      content = appendSignature(content, this.signature);
      // need to reload first, ensuring that the editorView is updated
      this.reloadState(content);

      if (contentWasEmpty) {
        this.handleEmptyBodyWithSignature();
      }
    },
    removeSignature() {
      if (!this.signature) return;
      let content = this.value;
      content = removeSignature(content, this.signature);
      // reload the state, ensuring that the editorView is updated
      this.reloadState(content);
    },
    isBodyEmpty(content) {
      // if content is undefined, we assume that the body is empty
      if (!content) return true;

      // if the signature is present, we need to remove it before checking
      // note that we don't update the editorView, so this is safe
      const bodyWithoutSignature = this.signature
        ? removeSignature(content, this.signature)
        : content;

      // trimming should remove all the whitespaces, so we can check the length
      return bodyWithoutSignature.trim().length === 0;
    },
    handleEmptyBodyWithSignature() {
      const { schema, tr } = this.state;

      // create a paragraph node and
      // start a transaction to append it at the end
      const paragraph = schema.nodes.paragraph.create();
      const paragraphTransaction = tr.insert(0, paragraph);
      this.editorView.dispatch(paragraphTransaction);

      // Set the focus at the start of the input field
      this.focusEditorInputField('start');
    },
    createEditorView() {
      this.editorView = new EditorView(this.$refs.editor, {
        state: this.state,
        clipboardTextSerializer: slice => {
          let text = '';
          let firstBlock = true;
          slice.content.forEach(node => {
            if (node.isBlock) {
              if (!firstBlock) text += '\n';
              firstBlock = false;
              node.content.forEach(inline => {
                if (inline.type.name === 'hard_break') text += '\n';
                else text += inline.textContent;
              });
            }
          });
          return text;
        },
        dispatchTransaction: tx => {
          this.state = this.state.apply(tx);
          this.editorView.updateState(this.state);
          this.emitOnChange();
        },
        handleDOMEvents: {
          keyup: () => {
            this.onKeyup();
          },
          keydown: (view, event) => {
            this.onKeydown(event);
          },
          focus: () => {
            this.onFocus();
          },
          click: () => {
            // this.isEditorMouseFocusedOnAnImage(); Enable it when the backend supports for message resize is done.
          },
          blur: () => {
            this.onBlur();
          },
          paste: (view, event) => {
            const data = event.clipboardData.files;
            if (data.length > 0) {
              event.preventDefault();
            }
          },
        },
      });
    },
    isEditorMouseFocusedOnAnImage() {
      if (!this.showImageResizeToolbar) {
        return;
      }
      this.selectedImageNode = document.querySelector(
        'img.ProseMirror-selectednode'
      );
      if (this.selectedImageNode) {
        this.isImageNodeSelected = !!this.selectedImageNode;
        // Get the position of the selected node
        this.setToolbarPosition();
      } else {
        this.isImageNodeSelected = false;
      }
    },
    setToolbarPosition() {
      const editorRect = this.editorRoot.getBoundingClientRect();
      const rect = this.selectedImageNode.getBoundingClientRect();
      this.toolbarPosition = {
        top: `${rect.top - editorRect.top - 30}px`,
        left: `${rect.left - editorRect.left - 4}px`,
      };
    },
    setURLWithQueryAndImageSize(size) {
      if (!this.showImageResizeToolbar) {
        return;
      }
      setURLWithQueryAndSize(this.selectedImageNode, size, this.editorView);
      this.isImageNodeSelected = false;
    },
    updateImgToolbarOnDelete() {
      // check if the selected node is present or not on keyup
      // this is needed because the user can select an image and then delete it
      // in that case, the selected node will be null and we need to hide the toolbar
      // otherwise, the toolbar will be visible even when the image is deleted and cause some errors
      if (this.selectedImageNode) {
        const hasImgSelectedNode = document.querySelector(
          'img.ProseMirror-selectednode'
        );
        if (!hasImgSelectedNode) {
          this.isImageNodeSelected = false;
        }
      }
    },
    isEnterToSendEnabled() {
      return isEditorHotKeyEnabled(this.uiSettings, 'enter');
    },
    isCmdPlusEnterToSendEnabled() {
      return isEditorHotKeyEnabled(this.uiSettings, 'cmd_enter');
    },
    handleKeyEvents(e) {
      if (hasPressedAltAndPKey(e)) {
        this.focusEditorInputField();
      }
      if (hasPressedAltAndLKey(e)) {
        this.focusEditorInputField();
      }
    },
    focusEditorInputField(pos = 'end') {
      const { tr } = this.editorView.state;

      const selection =
        pos === 'end' ? Selection.atEnd(tr.doc) : Selection.atStart(tr.doc);

      this.editorView.dispatch(tr.setSelection(selection));
      this.editorView.focus();
    },
    insertMentionNode(mentionItem) {
      if (!this.editorView) {
        return null;
      }
      const node = this.editorView.state.schema.nodes.mention.create({
        userId: mentionItem.id,
        userFullName: mentionItem.name,
      });

      this.insertNodeIntoEditor(node, this.range.from, this.range.to);
      this.$track(CONVERSATION_EVENTS.USED_MENTIONS);

      return false;
    },
    insertCannedResponse(cannedItem) {
      const updatedMessage = replaceVariablesInMessage({
        message: cannedItem,
        variables: this.variables,
      });

      if (!this.editorView) {
        return null;
      }

      let node = new MessageMarkdownTransformer(
        this.editorView.state.schema
      ).parse(updatedMessage);

      const from =
        node.textContent === updatedMessage
          ? this.range.from
          : this.range.from - 1;

      this.insertNodeIntoEditor(node, from, this.range.to);

      this.$track(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
      return false;
    },
    insertVariable(variable) {
      if (!this.editorView) {
        return null;
      }

      const content = `{{${variable}}}`;
      let node = this.editorView.state.schema.text(content);
      const { from, to } = this.range;

      this.insertNodeIntoEditor(node, from, to);
      this.showVariables = false;
      this.$track(CONVERSATION_EVENTS.INSERTED_A_VARIABLE);
      return false;
    },
    openFileBrowser() {
      this.$refs.imageUpload.click();
    },
    onFileChange() {
      const file = this.$refs.imageUpload.files[0];
      if (checkFileSizeLimit(file, MAXIMUM_FILE_UPLOAD_SIZE)) {
        this.uploadImageToStorage(file);
      } else {
        this.showAlert(
          this.$t(
            'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.IMAGE_UPLOAD_SIZE_ERROR',
            {
              size: MAXIMUM_FILE_UPLOAD_SIZE,
            }
          )
        );
      }

      this.$refs.imageUpload.value = '';
    },
    async uploadImageToStorage(file) {
      try {
        const { fileUrl } = await uploadFile(file);
        if (fileUrl) {
          this.onImageInsertInEditor(fileUrl);
        }
        this.showAlert(
          this.$t(
            'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.IMAGE_UPLOAD_SUCCESS'
          )
        );
      } catch (error) {
        this.showAlert(
          this.$t(
            'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.IMAGE_UPLOAD_ERROR'
          )
        );
      }
    },
    onImageInsertInEditor(fileUrl) {
      const { tr } = this.editorView.state;

      const insertData = findNodeToInsertImage(this.editorView.state, fileUrl);

      if (insertData) {
        this.editorView.dispatch(
          tr.insert(insertData.pos, insertData.node).scrollIntoView()
        );
        this.focusEditorInputField();
      }
    },

    emitOnChange() {
      this.$emit('input', this.contentFromEditor);
    },

    hideMentions() {
      this.showUserMentions = false;
    },
    resetTyping() {
      this.$emit('typing-off');
      this.idleTimer = null;
    },
    turnOffIdleTimer() {
      if (this.idleTimer) {
        clearTimeout(this.idleTimer);
      }
    },
    handleLineBreakWhenEnterToSendEnabled(event) {
      if (
        hasPressedEnterAndNotCmdOrShift(event) &&
        this.isEnterToSendEnabled() &&
        !this.overrideLineBreaks
      ) {
        event.preventDefault();
      }
    },
    handleLineBreakWhenCmdAndEnterToSendEnabled(event) {
      if (
        hasPressedCommandAndEnter(event) &&
        this.isCmdPlusEnterToSendEnabled() &&
        !this.overrideLineBreaks
      ) {
        event.preventDefault();
      }
    },
    onKeyup() {
      if (!this.idleTimer) {
        this.$emit('typing-on');
      }
      this.turnOffIdleTimer();
      this.idleTimer = setTimeout(
        () => this.resetTyping(),
        TYPING_INDICATOR_IDLE_TIME
      );
      // this.updateImgToolbarOnDelete(); Enable it when the backend supports for message resize is done.
    },
    onKeydown(event) {
      if (this.isEnterToSendEnabled()) {
        this.handleLineBreakWhenEnterToSendEnabled(event);
      }
      if (this.isCmdPlusEnterToSendEnabled()) {
        this.handleLineBreakWhenCmdAndEnterToSendEnabled(event);
      }
      this.handleLineBreakWhenEnterToSendEnabled(event);
    },
    onBlur() {
      this.turnOffIdleTimer();
      this.resetTyping();
      this.$emit('blur');
    },
    onFocus() {
      this.$emit('focus');
    },
    insertContentIntoEditor(content, defaultFrom = 0) {
      const from = defaultFrom || this.editorView.state.selection.from || 0;
      let node = new MessageMarkdownTransformer(
        this.editorView.state.schema
      ).parse(content);

      this.insertNodeIntoEditor(node, from, undefined);
    },
    insertNodeIntoEditor(node, from = 0, to = 0) {
      this.state = insertAtCursor(this.editorView, node, from, to);
      this.emitOnChange();
      this.$nextTick(() => {
        scrollCursorIntoView(this.editorView);
      });
    },
  },
};
</script>

<style lang="scss">
@import '~@chatwoot/prosemirror-schema/src/styles/base.scss';
.editor-root {
  min-height: 15rem;
}
.ProseMirror-menubar-wrapper {
  @apply flex flex-col;
  .ProseMirror-menubar {
    min-height: var(--space-two) !important;
    @apply -ml-2.5 pb-0 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-100;

    .ProseMirror-menu-active {
      @apply bg-slate-75 dark:bg-slate-800;
    }
  }
  > .ProseMirror {
    @apply p-0 break-words text-slate-800 dark:text-slate-100;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    p {
      @apply text-slate-800 dark:text-slate-100;
    }

    blockquote {
      @apply border-slate-400 dark:border-slate-500;

      p {
        @apply text-slate-600 dark:text-slate-400;
      }
    }

    ol li {
      @apply list-item list-decimal;
    }
  }
}

.editor-root {
  @apply w-full relative;
}

.ProseMirror-woot-style {
  @apply overflow-auto min-h-[5rem] max-h-[13rem];
}
:dir(rtl) {
  .ProseMirror-woot-style ul[dir='rtl'],
  .ProseMirror-woot-style ol[dir='rtl'] {
    padding-left: 0;
    padding-right: var(--space-normal);
    margin-right: 0;
  }

  .ProseMirror-woot-style ul[dir='ltr'],
  .ProseMirror-woot-style ol[dir='ltr'] {
    padding-left: var(--space-normal);
    padding-right: 0;
    margin-right: 0;
  }
}

.ProseMirror-prompt {
  @apply z-[9991] bg-slate-25 dark:bg-slate-700 rounded-md border border-solid border-slate-75 dark:border-slate-800;

  h5 {
    @apply dark:text-slate-25 text-slate-800;
  }
}

.prosemirror-mention-node {
  unicode-bidi: isolate;
}

.is-private {
  .prosemirror-mention-node {
    @apply font-medium bg-yellow-100 dark:bg-yellow-800 text-slate-900 dark:text-slate-25 py-0 px-1;
  }

  .ProseMirror-menubar-wrapper {
    .ProseMirror-menubar {
      @apply bg-yellow-100 dark:bg-yellow-800 text-slate-700 dark:text-slate-25;
    }

    > .ProseMirror {
      @apply text-slate-800 dark:text-slate-25;

      p {
        @apply text-slate-800 dark:text-slate-25;
      }
    }
  }
}

.message-editor {
  @apply border border-solid border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-md py-0 px-1 mb-0;
}

.editor_warning {
  @apply border border-solid border-red-400 dark:border-red-400;
}

.editor-warning__message {
  @apply text-red-400 dark:text-red-400 font-normal text-sm pt-1 pb-0 px-0;
}

.ProseMirror-textblock-dropdown {
  min-width: 3em;
}

.ProseMirror-menu {
  margin: 0 -4px;
  line-height: 1;
}

.ProseMirror-tooltip .ProseMirror-menu {
  width: -webkit-fit-content;
  width: fit-content;
  white-space: pre;
}

.ProseMirror-menuitem {
  margin-right: 0px;
  display: inline-block;
  min-width: 24px;
}

.ProseMirror-menuseparator {
  border-right: 1px solid #ddd;
  margin-right: 3px;
}

.ProseMirror-menu-dropdown,
.ProseMirror-menu-dropdown-menu {
  font-size: 90%;
  white-space: nowrap;
}
.ProseMirror-menu-dropdown.menu-item-fontFamily-option {
  max-width: unset;
}
.ProseMirror-menu-dropdown-menu.menu-item-fontFamily-option {
  max-height: 250px;
  overflow-y: auto;
}
.ProseMirror-menu-dropdown {
  vertical-align: 1px;
  cursor: pointer;
  position: relative;
  padding-right: 32px;
  height: 23px;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #1f2937;
  font-size: 14px;
  display: flex;
  align-items: center;

  &.menu-item-fontFamily-option {
    border-radius: 4px;
    padding-left: 8px;
    margin-left: 8px;
    margin-right: 16px;
  }

  &.menu-item-textColor-option {
    border-radius: 4px;
    margin-left: 8px;
    padding-left: 8px;
  }

  &.menu-item-alignment-option {
    border-radius: 4px;
    margin-left: 8px;
    padding-left: 8px;
    margin-right: 4px;

    .ProseMirror-icon {
      width: 24px !important;
      padding: 0;

      svg {
        width: 17px;
        height: 17px;
      }
    }
  }
}

.ProseMirror-icon {
  color: #6b7280;
  &.menu-item-strong-option {
    width: 24px !important;
  }
  &.menu-item-bulletList-option {
    width: 24px !important;
    margin-right: 16px;
  }
  &.menu-item-em-option {
    width: 24px !important;
    margin-inline: 4px;
  }
  &.menu-item-alignLeft-option,
  &.menu-item-alignRight-option,
  &.menu-item-alignCenter-option,
  &.menu-item-alignJustify-option,
  &.menu-item-orderedList-option,
  &.menu-item-code-option {
    width: 24px !important;
    margin-right: 4px;
  }
  &.menu-item-bulletList-option,
  &.menu-item-link-option {
    width: 24px !important;
  }
}

[dir='rtl'] {
  .ProseMirror-icon {
    &.menu-item-bulletList-option {
      margin-left: 16px;
      margin-right: 0;
    }
    &.menu-item-alignLeft-option,
    &.menu-item-alignRight-option,
    &.menu-item-alignCenter-option,
    &.menu-item-alignJustify-option,
    &.menu-item-orderedList-option,
    &.menu-item-code-option {
      margin-left: 4px;
      margin-right: 0;
    }
    &.menu-item-em-option {
      margin-right: 6px;
    }
  }
  .ProseMirror-menu-dropdown {
    &.menu-item-textColor-option {
      margin-left: 0;
      padding-left: 4px;
      margin-right: 16px;
      padding-right: 31px;
    }
    &.menu-item-alignment-option {
      margin-left: 4px;
      padding-left: 0;
      margin-right: 16px;
      padding-right: 32px;
    }
    &.menu-item-fontFamily-option {
      padding-right: 32px;
      padding-left: 8px;
      margin-right: 16px;
      margin-left: 16px;
    }
  }
}

.ProseMirror-menu-dropdown-menu.text-color-menu {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2px;
  padding: 4px;
  border: 0;
  border-radius: 8px;
  background: #f3f4f6;
  box-shadow:
    0 1px 3px 0 rgba(15, 24, 40, 0.1),
    0 1px 2px 0 rgba(15, 24, 40, 0.06);

  .ProseMirror-menu-dropdown-item {
    &:hover {
      @apply bg-gray-300 rounded-[4px];
    }
  }
}

.ProseMirror-menu-dropdown-menu.alignment-menu {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2px;
  padding: 4px;
  border: 0;
  border-radius: 8px;
  background: #f3f4f6;
  box-shadow:
    0 1px 3px 0 rgba(15, 24, 40, 0.1),
    0 1px 2px 0 rgba(15, 24, 40, 0.06);

  .ProseMirror-menu-dropdown-item {
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      @apply bg-gray-300 rounded-[4px];
    }

    .ProseMirror-icon {
      margin: 0 !important;
    }
  }
}

.ProseMirror-menu-dropdown-wrap {
  padding: 1px 0 1px 0px;
  display: inline-block;
  position: relative;
}

.ProseMirror-menu-dropdown:after {
  content: '';
  display: block;
  width: 12px;
  height: 7px;
  background-color: currentColor;
  opacity: 0.6;
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='7' viewBox='0 0 12 7' fill='none'%3E%3Cpath d='M5.65387 4.6L10.2539 -4.60609e-08L11.3076 1.05375L5.65387 6.7075L0.000116302 1.05375L1.05387 -4.48206e-07L5.65387 4.6Z' fill='black'/%3E%3C/svg%3E")
    no-repeat center / contain;
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='7' viewBox='0 0 12 7' fill='none'%3E%3Cpath d='M5.65387 4.6L10.2539 -4.60609e-08L11.3076 1.05375L5.65387 6.7075L0.000116302 1.05375L1.05387 -4.48206e-07L5.65387 4.6Z' fill='black'/%3E%3C/svg%3E")
    no-repeat center / contain;
  position: absolute;
  right: 8px;
  top: 40%;
}

.ProseMirror-menu-dropdown-menu,
.ProseMirror-menu-submenu {
  position: absolute;
  background: white;
  color: #666;
  border: 1px solid #aaa;
  padding: 2px;
}

.ProseMirror-menu-dropdown-menu {
  z-index: 15;
  min-width: 6em;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    @apply bg-[#c1c1c1] rounded-full;
    border: 2px solid transparent;
    background-clip: padding-box;
  }

  &::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400;
    border: 2px solid transparent;
    background-clip: padding-box;
  }
}

.ProseMirror-menu-dropdown-item:hover {
  background: #f2f2f2;
}

.ProseMirror-menu-submenu-wrap {
  position: relative;
  margin-right: -4px;
}

.ProseMirror-menu-submenu-label:after {
  content: '';
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
  border-left: 4px solid currentColor;
  opacity: 0.6;
  position: absolute;
  right: 4px;
  top: calc(50% - 4px);
}

.ProseMirror-menu-submenu {
  display: none;
  min-width: 4em;
  left: 100%;
  top: -3px;
}

.ProseMirror-menu-active {
  background: #eee;
  border-radius: 4px;
}

.ProseMirror-menu-disabled {
  opacity: 0.3;
}

.ProseMirror-menu-submenu-wrap:hover .ProseMirror-menu-submenu,
.ProseMirror-menu-submenu-wrap-active .ProseMirror-menu-submenu {
  display: block;
}

.ProseMirror-menubar {
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
  position: relative;
  min-height: 1em;
  color: #666;
  padding: 1px 6px;
  top: 0;
  left: 0;
  right: 0;
  border-bottom: 1px solid silver;
  background: white;
  z-index: 10;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
  overflow: visible;
}

.ProseMirror-icon {
  display: inline-block;
  line-height: 0.8;
  vertical-align: -2px; /* Compensate for padding */
  padding: 2px 8px;
  cursor: pointer;
}

.ProseMirror-menu-disabled.ProseMirror-icon {
  cursor: default;
}

.ProseMirror-icon svg {
  fill: currentColor;
  height: 1em;
}

.ProseMirror-icon span {
  vertical-align: text-top;
}

.ProseMirror-menu-dropdown-swatch {
  display: inline-block;
  width: 17px;
  height: 17px;
  border-radius: 4px;
  vertical-align: middle;
}

.ProseMirror-menubar {
  .ProseMirror-menuitem {
    .ProseMirror-menu-fontsize {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }
}

.ProseMirror-menu-fontsize-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.ProseMirror-icon:hover,
.ProseMirror-menu-dropdown:hover,
.ProseMirror-menu-fontsize-btn:hover {
  border-radius: 4px;
  height: 24px;
  background: #e5e7eb;
}

.ProseMirror-menu-fontsize-btn .ProseMirror-icon {
  padding: 0;
}

.ProseMirror-menu-fontsize-input {
  width: fit-content;
  height: 20px;
  border: none;
  background: transparent;
  color: #1f2937;
  font-size: 14px;
  text-align: center;
  appearance: textfield;
  -moz-appearance: textfield;
  margin: 0 !important;
  padding: 0px;
}

.ProseMirror-menu-fontsize-input::-webkit-outer-spin-button,
.ProseMirror-menu-fontsize-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0 !important;
}

.ProseMirror {
  ul[dir='rtl'],
  ol[dir='rtl'] {
    padding-right: var(--space-normal);
    padding-left: unset;
    margin-right: 0;

    ol,
    ul {
      padding-right: var(--space-normal);
      padding-left: unset;
      margin-right: 0;
    }
  }
}
</style>
